// src/pages/rh/AjouterEmploye.jsx
import React, { useState } from "react";
import {
    Card, Row, Col, Form, Button, Spinner, Badge,InputGroup
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { API_BASE_URL } from "../../constants/api";
import { COLORS } from "../../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function AjouterEmploye() {
    const navigate = useNavigate();
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });

    const [employe, setEmploye] = useState({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        adresse: '',
        date_naissance: '',
        lieu_naissance: '',
        nationalite: 'Gabonaise',
        sexe: 'M',
        situation_matrimoniale: 'Célibataire',
        nombre_enfants: 0,
        poste: '',
        departement: '',
        date_embauche: '',
        type_contrat: 'CDI',
        salaire_base: '',
        photo: null,
        cv: null,
        statut: 'actif'
    });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    const getCurrentUser = () => {
        try {
            const userStr = localStorage.getItem("user");
            if (userStr && userStr !== "undefined" && userStr !== "null") return JSON.parse(userStr);
        } catch (error) {
            console.error("Erreur parsing utilisateur:", error);
        }
        return null;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEmploye(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e, field) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                showToast('error', 'Fichier trop volumineux', 'Le fichier ne doit pas dépasser 5 Mo');
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setEmploye(prev => ({ ...prev, [field]: reader.result }));
                showToast('success', 'Fichier ajouté', `Le ${field === 'photo' ? 'photo' : 'CV'} a été chargé avec succès`);
            };
            reader.onerror = () => showToast('error', 'Erreur de lecture', 'Impossible de lire le fichier');
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!employe.nom || !employe.prenom || !employe.date_embauche) {
            showToast('error', 'Champ requis', 'Veuillez remplir tous les champs obligatoires');
            return;
        }
        setSaving(true);
        try {
            const token = localStorage.getItem("token");
            const currentUser = getCurrentUser();
            const dataToSend = {
                ...employe,
                created_by: currentUser?.id_utilisateur || currentUser?.id,
                nombre_enfants: parseInt(employe.nombre_enfants) || 0,
                salaire_base: parseFloat(employe.salaire_base) || 0
            };
            const response = await fetch(`${API_BASE_URL}/employes.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(dataToSend)
            });
            const result = await response.json();
            if (result.success) {
                showToast('success', 'Employé créé', `Matricule: ${result.matricule}`);
                setTimeout(() => navigate('/rh/employes'), 1500);
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de l'enregistrement");
            }
        } catch (err) {
            showToast('error', 'Erreur de connexion', "Impossible de contacter le serveur");
        } finally {
            setSaving(false);
        }
    };

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-user-add-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Nouvel employé</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-user-line"></i>
                                        Ajouter au personnel
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                        <i className="ri-add-line"></i>
                                        Nouveau
                                    </span>
                                </div>
                            </div>
                        </div>
                        <Button className="btn-modern-outline" onClick={() => navigate('/rh/employes')}>
                            <i className="ri-arrow-left-line"></i> Retour
                        </Button>
                    </div>

                    {/* ===== FORMULAIRE ===== */}
                    <Row className="justify-content-center">
                        <Col lg={10}>
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="card-header-gradient">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-user-add-line text-white fs-5"></i>
                                        <h5 className="mb-0 text-white">Formulaire d'ajout d'employé</h5>
                                        <span className="badge-header-light ms-2">
                                            <i className="ri-time-line"></i>Nouveau
                                        </span>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Form onSubmit={handleSubmit}>

                                        {/* ===== SECTION 1 : Informations personnelles ===== */}
                                        <div className="section-title-charte">
                                            <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                                <i className="ri-user-line"></i>
                                            </span>
                                            <h6>Informations personnelles</h6>
                                        </div>

                                        <Row className="g-3 mb-4">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        Nom <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-user-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="nom"
                                                            value={employe.nom}
                                                            onChange={handleChange}
                                                            required
                                                            placeholder="Nom de l'employé"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        Prénom <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-user-smile-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="prenom"
                                                            value={employe.prenom}
                                                            onChange={handleChange}
                                                            required
                                                            placeholder="Prénom de l'employé"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Email</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-mail-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="email"
                                                            name="email"
                                                            value={employe.email}
                                                            onChange={handleChange}
                                                            placeholder="email@exemple.com"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Téléphone</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-phone-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="tel"
                                                            name="telephone"
                                                            value={employe.telephone}
                                                            onChange={handleChange}
                                                            placeholder="+241 XX XX XX XX"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Adresse</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-map-pin-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={2}
                                                            name="adresse"
                                                            value={employe.adresse}
                                                            onChange={handleChange}
                                                            placeholder="Adresse complète"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Date de naissance</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-calendar-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="date"
                                                            name="date_naissance"
                                                            value={employe.date_naissance}
                                                            onChange={handleChange}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Lieu de naissance</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-map-pin-2-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="lieu_naissance"
                                                            value={employe.lieu_naissance}
                                                            onChange={handleChange}
                                                            placeholder="Ville de naissance"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Nationalité</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-earth-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="nationalite"
                                                            value={employe.nationalite}
                                                            onChange={handleChange}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Sexe</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-user-3-line"></i></InputGroup.Text>
                                                        <Form.Select
                                                            name="sexe"
                                                            value={employe.sexe}
                                                            onChange={handleChange}
                                                        >
                                                            <option value="M">Masculin</option>
                                                            <option value="F">Féminin</option>
                                                        </Form.Select>
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Situation matrimoniale</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-heart-line"></i></InputGroup.Text>
                                                        <Form.Select
                                                            name="situation_matrimoniale"
                                                            value={employe.situation_matrimoniale}
                                                            onChange={handleChange}
                                                        >
                                                            <option value="Célibataire">Célibataire</option>
                                                            <option value="Marié(e)">Marié(e)</option>
                                                            <option value="Divorcé(e)">Divorcé(e)</option>
                                                            <option value="Veuf(ve)">Veuf(ve)</option>
                                                        </Form.Select>
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Nombre d'enfants</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-parent-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="number"
                                                            name="nombre_enfants"
                                                            value={employe.nombre_enfants}
                                                            onChange={handleChange}
                                                            min="0"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        {/* ===== SECTION 2 : Informations professionnelles ===== */}
                                        <div className="section-title-charte">
                                            <span className="section-icon-charte" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                                <i className="ri-briefcase-line"></i>
                                            </span>
                                            <h6>Informations professionnelles</h6>
                                        </div>

                                        <Row className="g-3 mb-4">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Poste</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-briefcase-4-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="poste"
                                                            value={employe.poste}
                                                            onChange={handleChange}
                                                            placeholder="Intitulé du poste"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Département</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-building-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="departement"
                                                            value={employe.departement}
                                                            onChange={handleChange}
                                                            placeholder="Département"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        Date d'embauche <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-calendar-check-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="date"
                                                            name="date_embauche"
                                                            value={employe.date_embauche}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Type de contrat</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-file-text-line"></i></InputGroup.Text>
                                                        <Form.Select
                                                            name="type_contrat"
                                                            value={employe.type_contrat}
                                                            onChange={handleChange}
                                                        >
                                                            <option value="CDI">CDI</option>
                                                            <option value="CDD">CDD</option>
                                                            <option value="Stage">Stage</option>
                                                            <option value="Alternance">Alternance</option>
                                                        </Form.Select>
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Salaire de base (FCFA)</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-money-euro-circle-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="number"
                                                            name="salaire_base"
                                                            value={employe.salaire_base}
                                                            onChange={handleChange}
                                                            min="0"
                                                            step="1000"
                                                            placeholder="0"
                                                        />
                                                        <InputGroup.Text className="bg-light">FCFA</InputGroup.Text>
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Statut</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-flag-line"></i></InputGroup.Text>
                                                        <Form.Select
                                                            name="statut"
                                                            value={employe.statut}
                                                            onChange={handleChange}
                                                        >
                                                            <option value="actif">Actif</option>
                                                            <option value="inactif">Inactif</option>
                                                            <option value="suspendu">Suspendu</option>
                                                        </Form.Select>
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        {/* ===== SECTION 3 : Documents ===== */}
                                        <div className="section-title-charte">
                                            <span className="section-icon-charte" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                                <i className="ri-file-text-line"></i>
                                            </span>
                                            <h6>Documents</h6>
                                        </div>

                                        <Row className="g-3 mb-4">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Photo de profil</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-image-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={(e) => handleFileChange(e, 'photo')}
                                                        />
                                                    </InputGroup>
                                                    <Form.Text className="text-muted small">
                                                        <i className="ri-information-line me-1"></i>
                                                        Format JPG, PNG (max 5 Mo)
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">CV</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-file-pdf-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="file"
                                                            accept=".pdf,.doc,.docx"
                                                            onChange={(e) => handleFileChange(e, 'cv')}
                                                        />
                                                    </InputGroup>
                                                    <Form.Text className="text-muted small">
                                                        <i className="ri-information-line me-1"></i>
                                                        Format PDF, DOC, DOCX (max 5 Mo)
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        {/* ===== ACTIONS ===== */}
                                        <hr className="my-3" style={{ borderColor: '#e2e8f0' }} />
                                        <div className="d-flex justify-content-end gap-3 flex-wrap">
                                            <Button className="btn-modern-outline" onClick={() => navigate('/rh/employes')}>
                                                Annuler
                                            </Button>
                                            <Button type="submit" disabled={saving} className="btn-modern-success">
                                                {saving ? (
                                                    <><Spinner size="sm" className="me-2" />Enregistrement...</>
                                                ) : (
                                                    <><i className="ri-save-line me-2"></i>Enregistrer</>
                                                )}
                                            </Button>
                                        </div>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--success), #059669);
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--secondary), #0d8ba6);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.7rem;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }

                /* ===== SECTIONS ===== */
                .section-title-charte {
                    display: flex; align-items: center; gap: 0.75rem;
                    margin-bottom: 1rem; padding-bottom: 0.5rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                .section-title-charte h6 {
                    margin: 0; font-weight: 600;
                    font-size: 0.95rem; color: #1e293b;
                }
                .section-icon-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 1rem;
                }

                /* ===== INPUTS ===== */
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }
                .input-modern .input-group-text {
                    background: #f8fafc; border: 2px solid #e2e8f0;
                    border-right: none; color: var(--primary);
                    border-radius: 10px 0 0 10px;
                }
                .input-modern .form-control,
                .input-modern .form-select {
                    border: 2px solid #e2e8f0; border-left: none;
                    border-radius: 0 10px 10px 0;
                    padding: 0.6rem 0.9rem; font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-modern .form-control:focus,
                .input-modern .form-select:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }
                .input-modern .input-group-text.bg-light {
                    background: #f1f5f9 !important;
                    border-left: none;
                    border-radius: 0 10px 10px 0;
                    color: #64748b; font-weight: 600; font-size: 0.85rem;
                }

                /* ===== FILE UPLOAD ===== */
                .input-modern .form-control[type="file"] {
                    padding: 0.45rem 0.9rem;
                    cursor: pointer;
                }
                .input-modern .form-control[type="file"]::file-selector-button {
                    background: #f1f5f9;
                    border: none;
                    border-radius: 6px;
                    padding: 0.3rem 0.8rem;
                    margin-right: 0.8rem;
                    color: var(--primary);
                    font-weight: 500;
                    font-size: 0.8rem;
                    transition: background 0.2s;
                }
                .input-modern .form-control[type="file"]::file-selector-button:hover {
                    background: #e2e8f0;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-success {
                    background: linear-gradient(135deg, var(--success), #059669);
                    border: none; color: white; padding: 0.55rem 1.8rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(16,185,129,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-success:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(16,185,129,0.4); color: white;
                }
                .btn-modern-success:disabled { opacity: 0.6; transform: none; }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}